import React, { useState } from 'react';
import { Form, FormControl, FormLabel, Alert } from 'react-bootstrap';
import { SkuButtonFlowListener, SkuDetailFlowButtons } from './SkuDetailFlowButtons';
import { SkuParamListener } from '../../domain/models/SkuDetail';
import { RequestParam } from '../../domain/models/RequestParam';
import QRReader from '../qr-reader/QRReader';

interface SkuInputQRProps {
    label?: string;
    nameParam: string;
    placeholder?: string;
    buttonFlowListener?: SkuButtonFlowListener;
    skuParamListener?: SkuParamListener;
    initialValue?: string;
    required?: boolean;
}

export const SkuInputQR: React.FC<SkuInputQRProps> = ({
    label = "Código QR",
    nameParam,
    placeholder = "",
    buttonFlowListener,
    skuParamListener,
    initialValue = "",
    required = true,
}) => {
    const [value, setValue] = useState(initialValue);
    const [error, setError] = useState<string | null>(null);
    const [isScannedValue, setIsScannedValue] = useState(false);
    const qrReaderRef = React.useRef<any>(null);

    const validateInput = (inputValue: string): boolean => {
        // Validación requerido
        if (required && !inputValue.trim()) {
            setError("Este campo es obligatorio");
            return false;
        }

        setError(null);
        return true;
    };

    const notifyParamChange = (newValue: string) => {
        if (skuParamListener) {
            skuParamListener.addParam(nameParam, newValue);
        }
    };

    const handleChange = (newValue: string) => {
        setValue(newValue);
        const valid = validateInput(newValue);
        
        if (valid && newValue.trim()) {
            notifyParamChange(newValue);
        }
    };

    const handleQrResult = (result: string) => {
        setValue(result);
        setIsScannedValue(true);
        
        const valid = validateInput(result);
        
        if (valid && result.trim()) {
            notifyParamChange(result);
        }
    };

    const handleClearAndEditManually = () => {
        setValue('');
        setIsScannedValue(false);
        setError(null);
        qrReaderRef.current?.resetFileInput?.();
    };

    const handleContinue = () => {
        if (!validateInput(value)) {
            return;
        }

        if (buttonFlowListener?.onContinuePressed) {
            const param = new RequestParam(nameParam, value, label || "Referencia QR", true);
            buttonFlowListener.onContinuePressed(param);
        }
    };

    return (
        <div className="sku-input-qr p-3">
            {/* Título del componente */}
            <div className="mb-3">
                <h6 className="mb-0">{label}</h6>
            </div>

            {/* Sección de escaneo de QR - siempre visible */}
            <div className="mb-3">
                <QRReader 
                    ref={qrReaderRef}
                    onScan={handleQrResult}
                    onError={(error) => {
                        console.error('Error en el escáner QR:', error);
                        setError('Error al escanear el código QR.');
                    }}
                />
            </div>

            {/* Campo de entrada de la referencia */}
            <Form>
                <FormLabel className="fw-bold">Referencia:</FormLabel>
                <FormControl
                    type="text"
                    value={value}
                    onChange={(e) => {
                        handleChange(e.target.value);
                        setIsScannedValue(false);
                    }}
                    placeholder={placeholder}
                    isInvalid={!!error}
                    className="mb-2"
                />
                
                {isScannedValue && (
                    <div className="d-flex justify-content-end mb-2">
                        <button
                            className="btn btn-outline-danger btn-sm"
                            type="button"
                            onClick={handleClearAndEditManually}
                            title="Limpiar campo"
                        >
                            Limpiar
                        </button>
                    </div>
                )}
                
                {error && (
                    <Alert variant="danger" className="py-2">
                        <small>{error}</small>
                    </Alert>
                )}
            </Form>

            {/* Botones de flujo */}
            <div className="mt-4">
                <SkuDetailFlowButtons 
                    listener={{
                        onBackPressed: buttonFlowListener?.onBackPressed,
                        onContinuePressed: handleContinue
                    }}
                    disabled={!value.trim() || !!error}
                />
            </div>
        </div>
    );
};
